import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Database, CheckCircle, XCircle, ExternalLink, Copy, AlertCircle, Loader2, Zap, Shield, TrendingUp, Clock, BookOpen, X } from 'lucide-react';
import { useToast } from '../components/ui/Toast';
import { isSupabaseConfigured } from '../lib/supabase';
export function IntegrationsPage() {
  const {
    showToast
  } = useToast();
  // Google Sheets state
  const [googleApiKey, setGoogleApiKey] = useState('');
  const [googleSheetId, setGoogleSheetId] = useState('');
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [isTestingGoogle, setIsTestingGoogle] = useState(false);
  // Supabase state
  const [supabaseUrl, setSupabaseUrl] = useState('');
  const [supabaseKey, setSupabaseKey] = useState('');
  const [isSupabaseConnected, setIsSupabaseConnected] = useState(false);
  const [isTestingSupabase, setIsTestingSupabase] = useState(false);
  // Modal state
  const [showSupabaseGuide, setShowSupabaseGuide] = useState(false);
  const [showGoogleGuide, setShowGoogleGuide] = useState(false);
  // Load saved credentials
  useEffect(() => {
    const savedGoogleApiKey = localStorage.getItem('google_sheets_api_key') || '';
    const savedGoogleSheetId = localStorage.getItem('google_sheets_sheet_id') || '';
    setGoogleApiKey(savedGoogleApiKey);
    setGoogleSheetId(savedGoogleSheetId);
    setIsGoogleConnected(Boolean(savedGoogleApiKey && savedGoogleSheetId));
    const savedSupabaseUrl = localStorage.getItem('supabase_url') || '';
    const savedSupabaseKey = localStorage.getItem('supabase_anon_key') || '';
    setSupabaseUrl(savedSupabaseUrl);
    setSupabaseKey(savedSupabaseKey);
    setIsSupabaseConnected(isSupabaseConfigured() || Boolean(savedSupabaseUrl && savedSupabaseKey));
  }, []);
  const handleSaveGoogle = () => {
    if (!googleApiKey || !googleSheetId) {
      showToast('warning', 'Campos requeridos', 'Debes completar ambos campos');
      return;
    }
    localStorage.setItem('google_sheets_api_key', googleApiKey);
    localStorage.setItem('google_sheets_sheet_id', googleSheetId);
    setIsGoogleConnected(true);
    showToast('success', 'Google Sheets conectado', 'Recarga la página para aplicar los cambios');
  };
  const handleDisconnectGoogle = () => {
    localStorage.removeItem('google_sheets_api_key');
    localStorage.removeItem('google_sheets_sheet_id');
    setGoogleApiKey('');
    setGoogleSheetId('');
    setIsGoogleConnected(false);
    showToast('info', 'Desconectado', 'Google Sheets ha sido desconectado');
  };
  const testGoogleConnection = async () => {
    if (!googleApiKey || !googleSheetId) {
      showToast('warning', 'Campos requeridos', 'Completa ambos campos primero');
      return;
    }
    setIsTestingGoogle(true);
    try {
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${googleSheetId}/values/Orders!A1:A1?key=${googleApiKey}`;
      const response = await fetch(url);
      if (response.ok) {
        showToast('success', 'Conexión exitosa', 'Google Sheets configurado correctamente');
      } else {
        showToast('error', 'Error de conexión', 'Verifica tus credenciales');
      }
    } catch (error) {
      showToast('error', 'Error de conexión', 'No se pudo conectar a Google Sheets');
    } finally {
      setIsTestingGoogle(false);
    }
  };
  const handleSaveSupabase = () => {
    if (!supabaseUrl || !supabaseKey) {
      showToast('warning', 'Campos requeridos', 'Debes completar ambos campos');
      return;
    }
    localStorage.setItem('supabase_url', supabaseUrl);
    localStorage.setItem('supabase_anon_key', supabaseKey);
    setIsSupabaseConnected(true);
    showToast('success', 'Supabase conectado', 'Recarga la página para aplicar los cambios');
  };
  const handleDisconnectSupabase = () => {
    localStorage.removeItem('supabase_url');
    localStorage.removeItem('supabase_anon_key');
    setSupabaseUrl('');
    setSupabaseKey('');
    setIsSupabaseConnected(false);
    showToast('info', 'Desconectado', 'Supabase ha sido desconectado');
  };
  const testSupabaseConnection = async () => {
    if (!supabaseUrl || !supabaseKey) {
      showToast('warning', 'Campos requeridos', 'Completa ambos campos primero');
      return;
    }
    setIsTestingSupabase(true);
    try {
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        headers: {
          apikey: supabaseKey,
          Authorization: `Bearer ${supabaseKey}`
        }
      });
      if (response.ok || response.status === 404) {
        showToast('success', 'Conexión exitosa', 'Supabase configurado correctamente');
      } else {
        showToast('error', 'Error de conexión', 'Verifica tus credenciales');
      }
    } catch (error) {
      showToast('error', 'Error de conexión', 'No se pudo conectar a Supabase');
    } finally {
      setIsTestingSupabase(false);
    }
  };
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showToast('success', 'Copiado', `${label} copiado al portapapeles`);
  };
  const SQL_SCRIPT = `-- Tabla de Órdenes
CREATE TABLE orders (
  id TEXT PRIMARY KEY,
  order_no TEXT NOT NULL,
  order_date TIMESTAMP NOT NULL,
  delivery_date TIMESTAMP,
  type TEXT DEFAULT 'Envío',
  employee_name TEXT NOT NULL,
  employee_id TEXT,
  company TEXT NOT NULL,
  branch TEXT NOT NULL,
  product TEXT NOT NULL,
  quantity INTEGER DEFAULT 1,
  catalog_type TEXT DEFAULT 'Almuerzo',
  subtotal DECIMAL(10, 2) NOT NULL,
  discount DECIMAL(10, 2) DEFAULT 0,
  subsidy_amount DECIMAL(10, 2) DEFAULT 0,
  subsidy_applied_by TEXT,
  total DECIMAL(10, 2) NOT NULL,
  status TEXT DEFAULT 'Pendiente',
  label_status TEXT DEFAULT 'Pendiente',
  tax_exempt BOOLEAN DEFAULT false,
  created_at TIMESTAMP DEFAULT NOW()
);

-- Tabla de Empleados
CREATE TABLE employees (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL,
  email TEXT NOT NULL UNIQUE,
  username TEXT NOT NULL UNIQUE,
  password TEXT NOT NULL,
  position TEXT NOT NULL,
  company_id INTEGER NOT NULL,
  branch_id TEXT NOT NULL,
  status TEXT DEFAULT 'Active',
  permissions JSONB DEFAULT '{"canOrder": true, "canViewReports": false, "canManageMenu": false, "canManageEmployees": false}',
  created_at TIMESTAMP DEFAULT NOW()
);

-- Tabla de Reclamaciones
CREATE TABLE claims (
  id TEXT PRIMARY KEY,
  order_id TEXT NOT NULL,
  order_no TEXT NOT NULL,
  customer_name TEXT NOT NULL,
  company TEXT NOT NULL,
  branch TEXT NOT NULL,
  type TEXT NOT NULL,
  status TEXT DEFAULT 'Pendiente',
  priority TEXT DEFAULT 'Media',
  description TEXT NOT NULL,
  resolution TEXT,
  created_at TIMESTAMP DEFAULT NOW(),
  resolved_at TIMESTAMP,
  created_by TEXT NOT NULL,
  created_by_id TEXT NOT NULL
);

-- Tabla de Registro de Actividad
CREATE TABLE activity_logs (
  id SERIAL PRIMARY KEY,
  action TEXT NOT NULL,
  details TEXT NOT NULL,
  category TEXT NOT NULL,
  timestamp TIMESTAMP DEFAULT NOW()
);

-- Índices para mejorar rendimiento
CREATE INDEX idx_orders_company ON orders(company);
CREATE INDEX idx_orders_status ON orders(status);
CREATE INDEX idx_orders_date ON orders(order_date);
CREATE INDEX idx_employees_company ON employees(company_id);
CREATE INDEX idx_claims_order ON claims(order_id);

-- Habilitar RLS (Row Level Security)
ALTER TABLE orders ENABLE ROW LEVEL SECURITY;
ALTER TABLE employees ENABLE ROW LEVEL SECURITY;
ALTER TABLE claims ENABLE ROW LEVEL SECURITY;
ALTER TABLE activity_logs ENABLE ROW LEVEL SECURITY;

-- Políticas permisivas (acceso total para desarrollo)
CREATE POLICY "Allow all on orders" ON orders FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow all on employees" ON employees FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow all on claims" ON claims FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow all on activity_logs" ON activity_logs FOR ALL USING (true) WITH CHECK (true);`;
  return <div className="p-4 lg:p-8 max-w-6xl mx-auto" data-id="element-1471">
      <div className="mb-8" data-id="element-1472">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1473">
          Integraciones
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-1474">
          Conecta una base de datos para sincronizar pedidos en tiempo real
        </p>
      </div>

      {/* Comparison Table */}
      <Card className="mb-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 border-blue-200 dark:border-blue-800" data-id="element-1475">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-1476">
          ⚡ Comparación de Integraciones
        </h3>
        <div className="overflow-x-auto" data-id="element-1477">
          <table className="w-full text-sm" data-id="element-1478">
            <thead data-id="element-1479">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1480">
                <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1481">
                  Característica
                </th>
                <th className="text-center py-2 px-3 font-semibold text-purple-600 dark:text-purple-400" data-id="element-1482">
                  Supabase
                </th>
                <th className="text-center py-2 px-3 font-semibold text-green-600 dark:text-green-400" data-id="element-1483">
                  Google Sheets
                </th>
              </tr>
            </thead>
            <tbody className="text-gray-600 dark:text-gray-300" data-id="element-1484">
              <tr className="border-b border-gray-100 dark:border-gray-800" data-id="element-1485">
                <td className="py-2 px-3" data-id="element-1486">Velocidad</td>
                <td className="text-center py-2 px-3" data-id="element-1487">⚡ Instantáneo</td>
                <td className="text-center py-2 px-3" data-id="element-1488">🐢 5 segundos</td>
              </tr>
              <tr className="border-b border-gray-100 dark:border-gray-800" data-id="element-1489">
                <td className="py-2 px-3" data-id="element-1490">Tiempo Real</td>
                <td className="text-center py-2 px-3" data-id="element-1491">✅ Sí</td>
                <td className="text-center py-2 px-3" data-id="element-1492">⏱️ Polling</td>
              </tr>
              <tr className="border-b border-gray-100 dark:border-gray-800" data-id="element-1493">
                <td className="py-2 px-3" data-id="element-1494">Escalabilidad</td>
                <td className="text-center py-2 px-3" data-id="element-1495">🚀 Ilimitada</td>
                <td className="text-center py-2 px-3" data-id="element-1496">📊 Limitada</td>
              </tr>
              <tr className="border-b border-gray-100 dark:border-gray-800" data-id="element-1497">
                <td className="py-2 px-3" data-id="element-1498">Configuración</td>
                <td className="text-center py-2 px-3" data-id="element-1499">⚙️ 5 minutos</td>
                <td className="text-center py-2 px-3" data-id="element-1500">⚙️ 10 minutos</td>
              </tr>
              <tr className="border-b border-gray-100 dark:border-gray-800" data-id="element-1501">
                <td className="py-2 px-3" data-id="element-1502">Costo</td>
                <td className="text-center py-2 px-3" data-id="element-1503">💰 Gratis</td>
                <td className="text-center py-2 px-3" data-id="element-1504">💰 Gratis</td>
              </tr>
              <tr data-id="element-1505">
                <td className="py-2 px-3" data-id="element-1506">Recomendado para</td>
                <td className="text-center py-2 px-3" data-id="element-1507">🏢 Producción</td>
                <td className="text-center py-2 px-3" data-id="element-1508">🧪 Pruebas</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-1509">
        {/* Supabase Card */}
        <Card className="border-2 border-purple-200 dark:border-purple-800" data-id="element-1510">
          <div className="flex items-start justify-between mb-6" data-id="element-1511">
            <div className="flex items-center gap-3" data-id="element-1512">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-xl" data-id="element-1513">
                <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-1514" />
              </div>
              <div data-id="element-1515">
                <div className="flex items-center gap-2" data-id="element-1516">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1517">
                    Supabase
                  </h2>
                  <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-bold rounded-full" data-id="element-1518">
                    RECOMENDADO
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1519">
                  Base de datos profesional en tiempo real
                </p>
              </div>
            </div>

            {isSupabaseConnected ? <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/20 rounded-full" data-id="element-1520">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" data-id="element-1521" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-1522">
                  Conectado
                </span>
              </div> : <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full" data-id="element-1523">
                <XCircle className="w-4 h-4 text-gray-400" data-id="element-1524" />
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-1525">
                  No conectado
                </span>
              </div>}
          </div>

          {/* Benefits */}
          <div className="mb-6 space-y-2" data-id="element-1526">
            <div className="flex items-start gap-2 text-sm" data-id="element-1527">
              <TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" data-id="element-1528" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-1529">
                <strong data-id="element-1530">Súper rápido:</strong> Consultas instantáneas
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm" data-id="element-1531">
              <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" data-id="element-1532" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-1533">
                <strong data-id="element-1534">Tiempo real:</strong> Actualizaciones automáticas
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm" data-id="element-1535">
              <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" data-id="element-1536" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-1537">
                <strong data-id="element-1538">Seguro:</strong> Backups automáticos
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm" data-id="element-1539">
              <Database className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" data-id="element-1540" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-1541">
                <strong data-id="element-1542">Escalable:</strong> Millones de registros
              </span>
            </div>
          </div>

          {/* Configuration */}
          <div className="space-y-4 mb-6" data-id="element-1543">
            <div data-id="element-1544">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1545">
                Project URL
              </label>
              <div className="flex gap-2" data-id="element-1546">
                <input type="text" value={supabaseUrl} onChange={e => setSupabaseUrl(e.target.value)} placeholder="https://xxxxx.supabase.co" className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-1547" />
                {supabaseUrl && <Button variant="outline" size="sm" onClick={() => copyToClipboard(supabaseUrl, 'Supabase URL')} data-id="element-1548">
                    <Copy size={16} data-id="element-1549" />
                  </Button>}
              </div>
            </div>

            <div data-id="element-1550">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1551">
                anon public Key
              </label>
              <div className="flex gap-2" data-id="element-1552">
                <input type="password" value={supabaseKey} onChange={e => setSupabaseKey(e.target.value)} placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..." className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-1553" />
                {supabaseKey && <Button variant="outline" size="sm" onClick={() => copyToClipboard(supabaseKey, 'Supabase Key')} data-id="element-1554">
                    <Copy size={16} data-id="element-1555" />
                  </Button>}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3" data-id="element-1556">
            <Button onClick={handleSaveSupabase} disabled={!supabaseUrl || !supabaseKey} className="bg-purple-600 hover:bg-purple-700" data-id="element-1557">
              {isSupabaseConnected ? 'Actualizar' : 'Conectar'}
            </Button>

            <Button variant="outline" onClick={testSupabaseConnection} disabled={!supabaseUrl || !supabaseKey || isTestingSupabase} leftIcon={isTestingSupabase ? <Loader2 className="animate-spin" size={16} data-id="element-1559" /> : undefined} data-id="element-1558">
              {isTestingSupabase ? 'Probando...' : 'Probar'}
            </Button>

            {isSupabaseConnected && <Button variant="outline" onClick={handleDisconnectSupabase} className="text-red-600 border-red-300 hover:bg-red-50" data-id="element-1560">
                Desconectar
              </Button>}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800" data-id="element-1561">
            <button onClick={() => setShowSupabaseGuide(true)} className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium text-sm transition-colors" data-id="element-1562">
              <BookOpen size={16} data-id="element-1563" />
              Ver guía completa de configuración (5 minutos)
            </button>
          </div>
        </Card>

        {/* Google Sheets Card - Simplified for brevity */}
        <Card data-id="element-1564">
          <div className="flex items-start justify-between mb-6" data-id="element-1565">
            <div className="flex items-center gap-3" data-id="element-1566">
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-xl" data-id="element-1567">
                <Database className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-1568" />
              </div>
              <div data-id="element-1569">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1570">
                  Google Sheets
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1571">
                  Alternativa simple y visual
                </p>
              </div>
            </div>

            {isGoogleConnected ? <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/20 rounded-full" data-id="element-1572">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" data-id="element-1573" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-1574">
                  Conectado
                </span>
              </div> : <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full" data-id="element-1575">
                <XCircle className="w-4 h-4 text-gray-400" data-id="element-1576" />
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-1577">
                  No conectado
                </span>
              </div>}
          </div>

          <div className="mb-6 space-y-2" data-id="element-1578">
            <div className="flex items-start gap-2 text-sm" data-id="element-1579">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" data-id="element-1580" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-1581">
                <strong data-id="element-1582">Gratis:</strong> Sin costos mensuales
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm" data-id="element-1583">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" data-id="element-1584" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-1585">
                <strong data-id="element-1586">Visual:</strong> Ver datos en spreadsheet
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm" data-id="element-1587">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" data-id="element-1588" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-1589">
                <strong data-id="element-1590">Compartido:</strong> Múltiples usuarios
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm" data-id="element-1591">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" data-id="element-1592" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-1593">
                <strong data-id="element-1594">Backup:</strong> Versiones históricas
              </span>
            </div>
          </div>

          <div className="space-y-4 mb-6" data-id="element-1595">
            <div data-id="element-1596">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1597">
                Google API Key
              </label>
              <div className="flex gap-2" data-id="element-1598">
                <input type="password" value={googleApiKey} onChange={e => setGoogleApiKey(e.target.value)} placeholder="AIzaSyC1234567890abcdefghijklmnop" className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-1599" />
                {googleApiKey && <Button variant="outline" size="sm" onClick={() => copyToClipboard(googleApiKey, 'API Key')} data-id="element-1600">
                    <Copy size={16} data-id="element-1601" />
                  </Button>}
              </div>
            </div>

            <div data-id="element-1602">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1603">
                Google Sheet ID
              </label>
              <div className="flex gap-2" data-id="element-1604">
                <input type="text" value={googleSheetId} onChange={e => setGoogleSheetId(e.target.value)} placeholder="1ABC123XYZ456" className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-1605" />
                {googleSheetId && <Button variant="outline" size="sm" onClick={() => copyToClipboard(googleSheetId, 'Sheet ID')} data-id="element-1606">
                    <Copy size={16} data-id="element-1607" />
                  </Button>}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3" data-id="element-1608">
            <Button onClick={handleSaveGoogle} disabled={!googleApiKey || !googleSheetId} className="bg-green-600 hover:bg-green-700" data-id="element-1609">
              {isGoogleConnected ? 'Actualizar' : 'Conectar'}
            </Button>

            <Button variant="outline" onClick={testGoogleConnection} disabled={!googleApiKey || !googleSheetId || isTestingGoogle} leftIcon={isTestingGoogle ? <Loader2 className="animate-spin" size={16} data-id="element-1611" /> : undefined} data-id="element-1610">
              {isTestingGoogle ? 'Probando...' : 'Probar'}
            </Button>

            {isGoogleConnected && <Button variant="outline" onClick={handleDisconnectGoogle} className="text-red-600 border-red-300 hover:bg-red-50" data-id="element-1612">
                Desconectar
              </Button>}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800" data-id="element-1613">
            <button onClick={() => setShowGoogleGuide(true)} className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-medium text-sm transition-colors" data-id="element-1614">
              <BookOpen size={16} data-id="element-1615" />
              Ver guía de configuración (10 minutos)
            </button>
          </div>
        </Card>
      </div>

      {/* Important Notice */}
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg flex gap-3" data-id="element-1616">
        <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" data-id="element-1617" />
        <div className="text-sm text-yellow-800 dark:text-yellow-200" data-id="element-1618">
          <p className="font-medium mb-1" data-id="element-1619">⚠️ Importante</p>
          <p data-id="element-1620">
            Después de conectar cualquier integración, debes{' '}
            <strong data-id="element-1621">recargar la página completa</strong> (F5 o Ctrl+R) para que
            los cambios surtan efecto. El sistema detectará automáticamente la
            integración configurada y comenzará a usarla.
          </p>
        </div>
      </div>

      {/* Supabase Guide Modal */}
      {showSupabaseGuide && <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setShowSupabaseGuide(false);
    }} data-id="element-1622">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-gray-800" data-id="element-1623">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 flex-shrink-0" data-id="element-1624">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1625">
                🚀 Guía Completa - Supabase
              </h2>
              <button onClick={() => setShowSupabaseGuide(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" data-id="element-1626">
                <X size={24} className="text-gray-500" data-id="element-1627" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1" data-id="element-1628">
              <p className="text-base text-gray-600 dark:text-gray-300 mb-6" data-id="element-1629">
                Sigue estos 5 pasos para conectar Supabase en 5 minutos:
              </p>

              {/* Steps content - keeping all the existing steps */}
              {/* Step 1 */}
              <div className="mb-8" data-id="element-1630">
                <div className="flex items-center gap-3 mb-3" data-id="element-1631">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg" data-id="element-1632">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1633">
                    Crear cuenta en Supabase
                  </h3>
                </div>
                <div className="ml-13 space-y-2 text-base text-gray-600 dark:text-gray-300" data-id="element-1634">
                  <p data-id="element-1635">
                    1. Ve a{' '}
                    <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline inline-flex items-center gap-1 font-medium" data-id="element-1636">
                      supabase.com <ExternalLink size={14} data-id="element-1637" />
                    </a>
                  </p>
                  <p data-id="element-1638">
                    2. Haz clic en <strong data-id="element-1639">"Start your project"</strong>
                  </p>
                  <p data-id="element-1640">
                    3. Regístrate con <strong data-id="element-1641">GitHub</strong> o{' '}
                    <strong data-id="element-1642">Google</strong> (recomendado - 1 clic)
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="mb-8" data-id="element-1643">
                <div className="flex items-center gap-3 mb-3" data-id="element-1644">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg" data-id="element-1645">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1646">
                    Crear proyecto
                  </h3>
                </div>
                <div className="ml-13 space-y-2 text-base text-gray-600 dark:text-gray-300" data-id="element-1647">
                  <p data-id="element-1648">
                    1. Haz clic en <strong data-id="element-1649">"+ New Project"</strong>
                  </p>
                  <p data-id="element-1650">
                    2. <strong data-id="element-1651">Name:</strong>{' '}
                    <code className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded font-mono text-sm" data-id="element-1652">
                      la-cochera
                    </code>
                  </p>
                  <p data-id="element-1653">
                    3. <strong data-id="element-1654">Database Password:</strong> Copia y guarda esta
                    contraseña
                  </p>
                  <p data-id="element-1655">
                    4. <strong data-id="element-1656">Region:</strong> South America (São Paulo)
                  </p>
                  <p data-id="element-1657">
                    5. <strong data-id="element-1658">Plan:</strong> Free (gratis)
                  </p>
                  <p data-id="element-1659">
                    6. Haz clic en <strong data-id="element-1660">"Create new project"</strong>
                  </p>
                  <p className="text-yellow-600 dark:text-yellow-400 font-medium" data-id="element-1661">
                    ⏳ Espera 2-3 minutos mientras se crea
                  </p>
                </div>
              </div>

              {/* Step 3 - SQL */}
              <div className="mb-8" data-id="element-1662">
                <div className="flex items-center gap-3 mb-3" data-id="element-1663">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg" data-id="element-1664">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1665">
                    Crear las tablas (SQL)
                  </h3>
                </div>
                <div className="ml-13 space-y-3 text-base text-gray-600 dark:text-gray-300" data-id="element-1666">
                  <p data-id="element-1667">
                    1. Ve a <strong data-id="element-1668">SQL Editor</strong> en el menú lateral
                  </p>
                  <p data-id="element-1669">
                    2. Haz clic en <strong data-id="element-1670">"+ New query"</strong>
                  </p>
                  <p data-id="element-1671">3. Copia y pega este SQL completo:</p>

                  <div className="relative" data-id="element-1672">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs max-h-96 overflow-y-auto font-mono" data-id="element-1673">
                      <code data-id="element-1674">{SQL_SCRIPT}</code>
                    </pre>
                    <button onClick={() => {
                  copyToClipboard(SQL_SCRIPT, 'SQL Script');
                }} className="absolute top-3 right-3 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2 text-sm text-gray-300" data-id="element-1675">
                      <Copy size={16} data-id="element-1676" />
                      Copiar SQL
                    </button>
                  </div>

                  <p data-id="element-1677">
                    4. Haz clic en <strong data-id="element-1678">"Run"</strong> (botón abajo a la
                    derecha)
                  </p>
                  <p className="text-green-600 dark:text-green-400 font-medium" data-id="element-1679">
                    5. Deberías ver: ✅ "Success. No rows returned"
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="mb-8" data-id="element-1680">
                <div className="flex items-center gap-3 mb-3" data-id="element-1681">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg" data-id="element-1682">
                    4
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1683">
                    Copiar credenciales
                  </h3>
                </div>
                <div className="ml-13 space-y-3 text-base text-gray-600 dark:text-gray-300" data-id="element-1684">
                  <p data-id="element-1685">
                    1. Ve a <strong data-id="element-1686">Settings</strong> (⚙️) en el menú lateral
                  </p>
                  <p data-id="element-1687">
                    2. Haz clic en <strong data-id="element-1688">API</strong>
                  </p>
                  <p data-id="element-1689">3. Copia estos dos valores:</p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3" data-id="element-1690">
                    <div data-id="element-1691">
                      <p className="font-bold text-blue-900 dark:text-blue-100 mb-1" data-id="element-1692">
                        Project URL:
                      </p>
                      <code className="text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded block" data-id="element-1693">
                        https://xxxxx.supabase.co
                      </code>
                    </div>
                    <div data-id="element-1694">
                      <p className="font-bold text-blue-900 dark:text-blue-100 mb-1" data-id="element-1695">
                        anon public key:
                      </p>
                      <code className="text-xs bg-white dark:bg-gray-800 px-3 py-2 rounded block break-all" data-id="element-1696">
                        eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
                      </code>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-2" data-id="element-1697">
                        ⚠️ Es muy larga, asegúrate de copiar TODO
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="mb-8" data-id="element-1698">
                <div className="flex items-center gap-3 mb-3" data-id="element-1699">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg" data-id="element-1700">
                    5
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1701">
                    Conectar en La Cochera
                  </h3>
                </div>
                <div className="ml-13 space-y-2 text-base text-gray-600 dark:text-gray-300" data-id="element-1702">
                  <p data-id="element-1703">
                    1. Pega tu <strong data-id="element-1704">Project URL</strong> y{' '}
                    <strong data-id="element-1705">anon public key</strong> arriba
                  </p>
                  <p data-id="element-1706">
                    2. Haz clic en <strong data-id="element-1707">"Probar"</strong> para verificar
                  </p>
                  <p data-id="element-1708">
                    3. Haz clic en <strong data-id="element-1709">"Conectar"</strong>
                  </p>
                  <p data-id="element-1710">
                    4. <strong data-id="element-1711">Recarga la página</strong> (F5 o Ctrl+R)
                  </p>
                  <p className="text-green-600 dark:text-green-400 font-bold text-lg" data-id="element-1712">
                    5. ¡Listo! Tus pedidos se guardarán en Supabase 🎉
                  </p>
                </div>
              </div>

              {/* Verification */}
              <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-lg p-5 mb-6" data-id="element-1713">
                <h4 className="font-bold text-green-900 dark:text-green-100 mb-3 text-lg" data-id="element-1714">
                  ✅ Verificar que funciona:
                </h4>
                <ol className="text-base text-green-800 dark:text-green-200 space-y-2 list-decimal list-inside" data-id="element-1715">
                  <li data-id="element-1716">Abre la consola del navegador (F12)</li>
                  <li data-id="element-1717">Deberías ver: "🟢 Supabase configured..."</li>
                  <li data-id="element-1718">Crea una orden de prueba</li>
                  <li data-id="element-1719">Ve a Supabase → Table Editor → orders</li>
                  <li data-id="element-1720">¡Deberías ver tu orden ahí en tiempo real!</li>
                </ol>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-5" data-id="element-1721">
                <p className="text-base text-blue-900 dark:text-blue-100 font-bold mb-3" data-id="element-1722">
                  💡 Beneficios que ahora tienes:
                </p>
                <ul className="text-base text-blue-800 dark:text-blue-200 space-y-2" data-id="element-1723">
                  <li data-id="element-1724">
                    ⚡ <strong data-id="element-1725">Consultas instantáneas</strong> (sin esperas)
                  </li>
                  <li data-id="element-1726">
                    🔄 <strong data-id="element-1727">Actualizaciones en tiempo real</strong>{' '}
                    automáticas
                  </li>
                  <li data-id="element-1728">
                    🔒 <strong data-id="element-1729">Backups automáticos</strong> y seguros
                  </li>
                  <li data-id="element-1730">
                    📊 <strong data-id="element-1731">Escalable</strong> a millones de pedidos
                  </li>
                  <li data-id="element-1732">
                    🆓 <strong data-id="element-1733">Gratis para siempre</strong> (plan Free)
                  </li>
                </ul>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex-shrink-0" data-id="element-1734">
              <Button onClick={() => setShowSupabaseGuide(false)} fullWidth data-id="element-1735">
                Entendido, cerrar guía
              </Button>
            </div>
          </div>
        </div>}

      {/* Google Sheets Guide Modal - Similar structure */}
      {showGoogleGuide && <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setShowGoogleGuide(false);
    }} data-id="element-1736">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-gray-800" data-id="element-1737">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 flex-shrink-0" data-id="element-1738">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1739">
                📊 Guía Completa - Google Sheets
              </h2>
              <button onClick={() => setShowGoogleGuide(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" data-id="element-1740">
                <X size={24} className="text-gray-500" data-id="element-1741" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1" data-id="element-1742">
              <p className="text-base text-gray-600 dark:text-gray-300 mb-6" data-id="element-1743">
                Sigue estos pasos para conectar Google Sheets en 10 minutos:
              </p>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-lg p-5" data-id="element-1744">
                <p className="text-base text-yellow-900 dark:text-yellow-100" data-id="element-1745">
                  <strong data-id="element-1746">⚠️ Nota:</strong> Para ver la estructura completa de
                  las hojas y headers, revisa el archivo{' '}
                  <code className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 rounded" data-id="element-1747">
                    GOOGLE_SHEETS_SETUP.md
                  </code>{' '}
                  en el proyecto.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex-shrink-0" data-id="element-1748">
              <Button onClick={() => setShowGoogleGuide(false)} fullWidth data-id="element-1749">
                Entendido, cerrar guía
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}